import React, { useEffect, useState } from "react";
import { errorMessage } from "./workApi";
import "./orgChart.css";

const ADD_NEW = "__add_new__";

/**
 * Department picker backed by the company's departments master (departments_mt). The value is the
 * department name; the API links it to the master row on save. Admins and owners can add a new
 * department inline. ``SelectComponent`` lets the portal pass its styled PortalSelect.
 */
function DepartmentSelect({ api, value, onChange, id, required = false, SelectComponent = "select" }) {
  const [departments, setDepartments] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .departments()
      .then((rows) => !cancelled && setDepartments((rows || []).filter((d) => d.Is_Active !== false)))
      .catch(() => !cancelled && setDepartments([]));
    return () => {
      cancelled = true;
    };
  }, [api]);

  const names = (departments || []).map((d) => d.Department_Name);
  const current = (value || "").trim();
  const match = names.find((n) => n.toLowerCase() === current.toLowerCase());
  const legacy = current && !match;

  const handleSelect = (e) => {
    const next = e.target.value;
    if (next === ADD_NEW) {
      setAdding(true);
      setError("");
      return;
    }
    onChange(next);
  };

  const addDepartment = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    setError("");
    try {
      const existing = (departments || []).find((d) => d.Department_Name.toLowerCase() === name.toLowerCase());
      const dept = existing || (await api.createDepartment({ Department_Name: name }));
      if (!existing) {
        setDepartments((rows) =>
          [...(rows || []), dept].sort((a, b) => a.Department_Name.localeCompare(b.Department_Name))
        );
      }
      onChange(dept.Department_Name);
      setAdding(false);
      setNewName("");
    } catch (err) {
      setError(errorMessage(err, "Could not add the department"));
    } finally {
      setSaving(false);
    }
  };

  if (adding) {
    return (
      <div>
        <div className="oc-dept-add">
          <input
            id={id}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addDepartment();
              }
              if (e.key === "Escape") setAdding(false);
            }}
            placeholder="New department name"
            maxLength={100}
            aria-label="New department name"
            autoFocus
          />
          <button type="button" className="oc-dept-add__save" onClick={addDepartment} disabled={saving || !newName.trim()}>
            {saving ? "Adding…" : "Add"}
          </button>
          <button
            type="button"
            className="oc-dept-add__cancel"
            onClick={() => {
              setAdding(false);
              setError("");
            }}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
        {error ? (
          <div className="oc-dept-add__error" role="alert">
            {error}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <SelectComponent id={id} value={match || current} onChange={handleSelect} required={required}>
      <option value="">{departments === null ? "Loading…" : "Select department"}</option>
      {legacy ? <option value={current}>{current}</option> : null}
      {names.map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
      <option value={ADD_NEW}>+ Add new department</option>
    </SelectComponent>
  );
}

export default DepartmentSelect;
